import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FaAndroid,
  FaApple,
  FaBook,
  FaBookOpen,
  FaKeyboard,
  FaQrcode,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import appStoreQr from './QrCodes/AppStore.png';
import playStoreQr from './QrCodes/PlayStore.png';

import DailyVerse from '../DailyVerse/DailyVerse';
import Footer from '../Footer/Footer';
import { AuthContext } from '../Authentication/AuthContext';
import TamilKeyboard from '../Bible/SearchWord/TamilKeyboard';
import {
  getBookList,
  getBookNameByLanguage,
  getTestamentConfig,
  normalizeBookKey,
} from '../Bible/BookNames';
import {
  getBookChapterCount,
  getChapterVerses,
  prefetchWholeBookInBackground,
  startBibleDownloadCache,
} from '../Bible/DownloadCache';
import './Home.css';

const ALL_VALUE = 'all';
const SELECT_TEXT = 'Select';

const DEFAULT_SELECTED_LANGUAGES = ['Tamil'];

const LANGUAGE_OPTIONS = [
  { value: 'Tamil', label: 'Tamil' },
  { value: 'English', label: 'English' },
  { value: 'Deutsch', label: 'Deutsch' },
];

const IOS_APP_LINK = 'https://apps.apple.com/app/id6670354919';
const ANDROID_APP_LINK =
  'https://play.google.com/store/apps/details?id=com.tamilholybible';

const getAllSelectText = (appLanguage) => {
  if (appLanguage === 'English') return 'All / Select';
  if (appLanguage === 'Deutsch') return 'Alle / Select';
  return 'அனைத்தும் / Select';
};

function Home() {
  const { language } = useContext(AuthContext);
  const navigate = useNavigate();

  const homeMainScrollRef = useRef(null);
  const wordInputRef = useRef(null);
  const wordInputRowRef = useRef(null);
  const homeWordCardRef = useRef(null);
  const homeWordKeyboardRef = useRef(null);
  const homeWordKeyboardToggleRef = useRef(null);
  const pageTouchLastYRef = useRef(0);
  const bodyScrollYRef = useRef(0);

  const oldConfig = useMemo(() => getTestamentConfig('old'), []);
  const newConfig = useMemo(() => getTestamentConfig('new'), []);
  const oldBooks = useMemo(() => getBookList('old'), []);
  const newBooks = useMemo(() => getBookList('new'), []);

  const [openSelectId, setOpenSelectId] = useState('');
  const [expandedHomeBookId, setExpandedHomeBookId] = useState('');
  const [openingHomeChapterId, setOpeningHomeChapterId] = useState('');

  const [verseForm, setVerseForm] = useState({
    testamentType: '',
    book: '',
    chapter: '',
    verse: '',
  });

  const [wordForm, setWordForm] = useState({
    testamentType: '',
    book: ALL_VALUE,
    chapter: ALL_VALUE,
    word: '',
  });

  const [verseLanguages, setVerseLanguages] = useState(
    DEFAULT_SELECTED_LANGUAGES
  );
  const [wordLanguages, setWordLanguages] = useState(
    DEFAULT_SELECTED_LANGUAGES
  );

  const [showHomeWordKeyboard, setShowHomeWordKeyboard] = useState(false);
  const [homeKeyboardResetSignal, setHomeKeyboardResetSignal] = useState(0);
  const [homeKeyboardPosition, setHomeKeyboardPosition] = useState({
    top: 0,
    maxHeight: 0,
    ready: false,
  });

  const text = useMemo(() => {
    if (language === 'English') {
      return {
        title: 'Holy Bible',
        verseTitle: 'Find Verse',
        wordTitle: 'Find Word',
        testament: 'Testament',
        book: 'Book',
        chapter: 'Chapter',
        verse: 'Verse',
        word: 'Word',
        language: 'Language',
        wordPlaceholder: 'Type the word',
        versePlaceholder: 'Example: 1 or 1-14',
        submit: 'Submit',
        reset: 'Reset',
        keyboard: 'Keyboard',
        closeKeyboard: 'Close keyboard',
        iosTitle: 'iOS App',
        androidTitle: 'Android App',
        scanText: 'Click or scan to download',
      };
    }

    if (language === 'Deutsch') {
      return {
        title: 'Die Bibel',
        verseTitle: 'Vers suchen',
        wordTitle: 'Wort suchen',
        testament: 'Testament',
        book: 'Buch',
        chapter: 'Kapitel',
        verse: 'Vers',
        word: 'Wort',
        language: 'Sprache',
        wordPlaceholder: 'Wort eingeben',
        versePlaceholder: 'Example: 1 or 1-14',
        submit: 'Submit',
        reset: 'Reset',
        keyboard: 'Tastatur',
        closeKeyboard: 'Tastatur schließen',
        iosTitle: 'iOS App',
        androidTitle: 'Android App',
        scanText: 'Klicken oder scannen zum Download',
      };
    }

    return {
      title: 'பரிசுத்த வேதாகமம்',
      verseTitle: 'வசனம் தேடல்',
      wordTitle: 'சொல் தேடல்',
      testament: 'ஏற்பாடு',
      book: 'ஆகமம்',
      chapter: 'அதிகாரம்',
      verse: 'வசன இலக்கம்',
      word: 'வார்த்தை',
      language: 'மொழி',
      wordPlaceholder: 'சொல்லை உள்ளிடவும்',
      versePlaceholder: 'Example: 1 or 1-14',
      submit: 'Submit',
      reset: 'Reset',
      keyboard: 'விசைப்பலகை',
      closeKeyboard: 'விசைப்பலகையை மூடு',
      iosTitle: 'iOS App',
      androidTitle: 'Android App',
      scanText: 'பதிவிறக்க கிளிக் அல்லது ஸ்கேன் செய்யுங்கள்',
    };
  }, [language]);

  const allSelectText = useMemo(() => getAllSelectText(language), [language]);

  useEffect(() => {
    startBibleDownloadCache();
  }, []);

  useEffect(() => {
    const handleOutsideSelectClick = (event) => {
      if (event.target.closest('.home-custom-select')) return;
      setOpenSelectId('');
    };

    document.addEventListener('mousedown', handleOutsideSelectClick, true);
    document.addEventListener('touchstart', handleOutsideSelectClick, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideSelectClick, true);
      document.removeEventListener('touchstart', handleOutsideSelectClick, true);
    };
  }, []);

  useEffect(() => {
    const updateHomeViewportVariables = () => {
      const visualViewport = window.visualViewport;
      const visualViewportHeight =
        visualViewport && visualViewport.height
          ? visualViewport.height
          : window.innerHeight;

      const bottomBrowserSpace =
        visualViewport && visualViewport.height
          ? Math.max(
              0,
              window.innerHeight -
                visualViewport.height -
                visualViewport.offsetTop
            )
          : 0;

      document.documentElement.style.setProperty(
        '--home-visual-viewport-height',
        `${Math.round(visualViewportHeight)}px`
      );

      document.documentElement.style.setProperty(
        '--home-mobile-bottom-space',
        `${Math.round(bottomBrowserSpace)}px`
      );
    };

    updateHomeViewportVariables();

    window.addEventListener('resize', updateHomeViewportVariables);
    window.addEventListener('orientationchange', updateHomeViewportVariables);

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        updateHomeViewportVariables
      );
      window.visualViewport.addEventListener(
        'scroll',
        updateHomeViewportVariables
      );
    }

    return () => {
      window.removeEventListener('resize', updateHomeViewportVariables);
      window.removeEventListener(
        'orientationchange',
        updateHomeViewportVariables
      );

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateHomeViewportVariables
        );
        window.visualViewport.removeEventListener(
          'scroll',
          updateHomeViewportVariables
        );
      }

      document.documentElement.style.removeProperty(
        '--home-visual-viewport-height'
      );
      document.documentElement.style.removeProperty(
        '--home-mobile-bottom-space'
      );
    };
  }, []);

  useEffect(() => {
    bodyScrollYRef.current = window.scrollY || window.pageYOffset || 0;

    document.documentElement.classList.add('home-scroll-lock');
    document.body.classList.add('home-scroll-lock');
    document.body.style.top = `-${bodyScrollYRef.current}px`;

    return () => {
      document.documentElement.classList.remove('home-scroll-lock');
      document.body.classList.remove('home-scroll-lock');
      document.body.style.top = '';
      window.scrollTo(0, bodyScrollYRef.current);
    };
  }, []);

  useEffect(() => {
    const getScrollableElement = (target) => {
      const pageElement = homeMainScrollRef.current;

      if (!pageElement || !target) return null;
      if (!pageElement.contains(target)) return null;

      let currentElement = target;

      while (currentElement && currentElement !== document.body) {
        const style = window.getComputedStyle(currentElement);
        const canScrollY =
          /(auto|scroll)/.test(style.overflowY) &&
          currentElement.scrollHeight > currentElement.clientHeight + 1;

        if (canScrollY) return currentElement;
        if (currentElement === pageElement) break;

        currentElement = currentElement.parentElement;
      }

      return pageElement.scrollHeight > pageElement.clientHeight + 1
        ? pageElement
        : null;
    };

    const preventWindowDrag = () => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    const handleTouchStart = (event) => {
      if (!event.touches || event.touches.length !== 1) return;
      pageTouchLastYRef.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      if (!event.touches || event.touches.length !== 1) return;

      const scrollableElement = getScrollableElement(event.target);

      if (!scrollableElement) {
        event.preventDefault();
        preventWindowDrag();
        return;
      }

      const currentY = event.touches[0].clientY;
      const movementY = currentY - pageTouchLastYRef.current;
      pageTouchLastYRef.current = currentY;

      const maxScrollTop =
        scrollableElement.scrollHeight - scrollableElement.clientHeight;

      if (maxScrollTop <= 1) {
        event.preventDefault();
        preventWindowDrag();
        return;
      }

      const isAtTop = scrollableElement.scrollTop <= 0;
      const isAtBottom = scrollableElement.scrollTop >= maxScrollTop - 1;

      if ((isAtTop && movementY > 0) || (isAtBottom && movementY < 0)) {
        event.preventDefault();
        preventWindowDrag();
      }
    };

    const handleWheel = (event) => {
      const scrollableElement = getScrollableElement(event.target);

      if (!scrollableElement) {
        event.preventDefault();
        preventWindowDrag();
        return;
      }

      const maxScrollTop =
        scrollableElement.scrollHeight - scrollableElement.clientHeight;

      const isAtTop = scrollableElement.scrollTop <= 0;
      const isAtBottom = scrollableElement.scrollTop >= maxScrollTop - 1;

      if (
        (isAtTop && event.deltaY < 0) ||
        (isAtBottom && event.deltaY > 0)
      ) {
        event.preventDefault();
        preventWindowDrag();
      }
    };

    const handleGesture = (event) => {
      event.preventDefault();
    };

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
      capture: true,
    });

    document.addEventListener('touchmove', handleTouchMove, {
      passive: false,
      capture: true,
    });

    document.addEventListener('wheel', handleWheel, {
      passive: false,
      capture: true,
    });

    document.addEventListener('gesturestart', handleGesture, {
      passive: false,
      capture: true,
    });

    document.addEventListener('gesturechange', handleGesture, {
      passive: false,
      capture: true,
    });

    document.addEventListener('gestureend', handleGesture, {
      passive: false,
      capture: true,
    });

    window.addEventListener('scroll', preventWindowDrag, {
      passive: true,
    });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, true);
      document.removeEventListener('touchmove', handleTouchMove, true);
      document.removeEventListener('wheel', handleWheel, true);
      document.removeEventListener('gesturestart', handleGesture, true);
      document.removeEventListener('gesturechange', handleGesture, true);
      document.removeEventListener('gestureend', handleGesture, true);
      window.removeEventListener('scroll', preventWindowDrag);
    };
  }, []);

  useEffect(() => {
    const stopHeaderTouchDrag = (event) => {
      if (!event.touches || event.touches.length !== 1) return;
      event.preventDefault();
    };

    const headerElements = [
      document.querySelector('.top-header-shell'),
      document.querySelector('.top-header'),
      document.querySelector('.contact-info'),
      document.querySelector('.daily-verse-header'),
    ].filter(Boolean);

    headerElements.forEach((headerElement) => {
      headerElement.addEventListener('touchmove', stopHeaderTouchDrag, {
        passive: false,
      });
    });

    return () => {
      headerElements.forEach((headerElement) => {
        headerElement.removeEventListener('touchmove', stopHeaderTouchDrag);
      });
    };
  }, []);

  const moveHomeWordCursorToEnd = useCallback((nextWord) => {
    window.requestAnimationFrame(() => {
      const input = wordInputRef.current;

      if (!input) return;

      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }

      if (typeof input.setSelectionRange === 'function') {
        input.setSelectionRange(nextWord.length, nextWord.length);
      }
    });
  }, []);

  const updateHomeKeyboardPosition = useCallback(() => {
    if (
      !homeWordCardRef.current ||
      !wordInputRowRef.current ||
      !homeWordKeyboardRef.current
    ) {
      return;
    }

    const cardRect = homeWordCardRef.current.getBoundingClientRect();
    const wordRowRect = wordInputRowRef.current.getBoundingClientRect();
    const keyboardRect = homeWordKeyboardRef.current.getBoundingClientRect();

    const edgePadding = 16;
    const gap = 10;
    const minimumUsableHeight = 92;

    const wordTop = wordRowRect.top - cardRect.top;
    const wordBottom = wordRowRect.bottom - cardRect.top;
    const naturalKeyboardHeight = keyboardRect.height || 180;

    const aboveAvailableHeight = Math.floor(wordTop - edgePadding - gap);
    const belowAvailableHeight = Math.floor(
      cardRect.height - wordBottom - edgePadding - gap
    );

    let nextTop = edgePadding;
    let nextMaxHeight = Math.max(60, aboveAvailableHeight);

    if (
      aboveAvailableHeight >= minimumUsableHeight ||
      aboveAvailableHeight >= belowAvailableHeight
    ) {
      nextMaxHeight = Math.max(60, aboveAvailableHeight);

      const visibleKeyboardHeight = Math.min(
        naturalKeyboardHeight,
        nextMaxHeight
      );

      nextTop = Math.max(edgePadding, wordTop - gap - visibleKeyboardHeight);
    } else {
      nextMaxHeight = Math.max(60, belowAvailableHeight);

      const lowestSafeTop = Math.max(
        edgePadding,
        cardRect.height - edgePadding - nextMaxHeight
      );

      nextTop = Math.min(wordBottom + gap, lowestSafeTop);
    }

    setHomeKeyboardPosition({
      top: Math.round(nextTop),
      maxHeight: Math.round(nextMaxHeight),
      ready: true,
    });
  }, []);

  useEffect(() => {
    if (!showHomeWordKeyboard) {
      setHomeKeyboardPosition({
        top: 0,
        maxHeight: 0,
        ready: false,
      });
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      updateHomeKeyboardPosition();
    });

    window.addEventListener('resize', updateHomeKeyboardPosition);
    window.addEventListener('orientationchange', updateHomeKeyboardPosition);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateHomeKeyboardPosition);
      window.removeEventListener('orientationchange', updateHomeKeyboardPosition);
    };
  }, [showHomeWordKeyboard, updateHomeKeyboardPosition]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!showHomeWordKeyboard) return;

      if (
        homeWordKeyboardRef.current &&
        homeWordKeyboardRef.current.contains(event.target)
      ) {
        return;
      }

      if (
        homeWordKeyboardToggleRef.current &&
        homeWordKeyboardToggleRef.current.contains(event.target)
      ) {
        return;
      }

      setShowHomeWordKeyboard(false);
    };

    document.addEventListener('mousedown', handleOutsideClick, true);
    document.addEventListener('touchstart', handleOutsideClick, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick, true);
      document.removeEventListener('touchstart', handleOutsideClick, true);
    };
  }, [showHomeWordKeyboard]);

  const oldLabel = getBookNameByLanguage(oldConfig.titleKey, language);
  const newLabel = getBookNameByLanguage(newConfig.titleKey, language);

  const verseTestamentOptions = useMemo(
    () => [
      { value: '', label: SELECT_TEXT },
      {
        value: 'old',
        label: getBookNameByLanguage(oldConfig.titleKey, language),
      },
      {
        value: 'new',
        label: getBookNameByLanguage(newConfig.titleKey, language),
      },
    ],
    [language, newConfig.titleKey, oldConfig.titleKey]
  );

  const wordTestamentOptions = useMemo(
    () => [
      { value: '', label: allSelectText },
      {
        value: 'old',
        label: getBookNameByLanguage(oldConfig.titleKey, language),
      },
      {
        value: 'new',
        label: getBookNameByLanguage(newConfig.titleKey, language),
      },
    ],
    [allSelectText, language, newConfig.titleKey, oldConfig.titleKey]
  );

  const getBooksForTestament = useCallback(
    (testamentType) => {
      if (testamentType === 'old') return oldBooks;
      if (testamentType === 'new') return newBooks;
      return [];
    },
    [newBooks, oldBooks]
  );

  const getBookOptions = useCallback(
    (testamentType, includeAll = false) => {
      const books = getBooksForTestament(testamentType);

      const options = includeAll
        ? [{ value: ALL_VALUE, label: allSelectText }]
        : [{ value: '', label: SELECT_TEXT }];

      books.forEach((bookName) => {
        const bookKey = normalizeBookKey(bookName);

        options.push({
          value: bookKey,
          label: getBookNameByLanguage(bookName, language),
        });
      });

      return options;
    },
    [allSelectText, getBooksForTestament, language]
  );

  const getChapterOptions = useCallback(
    (testamentType, bookKey, includeAll = false) => {
      const options = includeAll
        ? [{ value: ALL_VALUE, label: allSelectText }]
        : [{ value: '', label: SELECT_TEXT }];

      if (!testamentType || !bookKey || bookKey === ALL_VALUE) {
        return options;
      }

      const chapterCount = getBookChapterCount(testamentType, bookKey);

      for (
        let chapterNumber = 1;
        chapterNumber <= chapterCount;
        chapterNumber += 1
      ) {
        options.push({
          value: String(chapterNumber),
          label: String(chapterNumber),
        });
      }

      return options;
    },
    [allSelectText]
  );

  const verseBookOptions = useMemo(
    () => getBookOptions(verseForm.testamentType, false),
    [getBookOptions, verseForm.testamentType]
  );

  const verseChapterOptions = useMemo(
    () => getChapterOptions(verseForm.testamentType, verseForm.book, false),
    [getChapterOptions, verseForm.book, verseForm.testamentType]
  );

  const wordBookOptions = useMemo(
    () => getBookOptions(wordForm.testamentType, true),
    [getBookOptions, wordForm.testamentType]
  );

  const wordChapterOptions = useMemo(
    () => getChapterOptions(wordForm.testamentType, wordForm.book, true),
    [getChapterOptions, wordForm.book, wordForm.testamentType]
  );

  const getExpandedBookId = useCallback((testamentType, bookKey) => {
    return `${testamentType}:${bookKey}`;
  }, []);

  const getChapterPath = useCallback((config, bookKey, chapterNumber) => {
    return `/${config.routeSegment}/chapters/${bookKey}/${chapterNumber}`;
  }, []);

  const handleBookPrefetch = useCallback((bookName, testamentType) => {
    const bookKey = normalizeBookKey(bookName);
    prefetchWholeBookInBackground(testamentType, bookKey);
  }, []);

  const prefetchChapter = useCallback(
    async (testamentType, bookKey, chapterNumber) => {
      if (!testamentType || !bookKey || !chapterNumber) return [];

      const loadedVerses = await getChapterVerses(
        testamentType,
        bookKey,
        chapterNumber
      );

      return Array.isArray(loadedVerses) ? loadedVerses : [];
    },
    []
  );

  const handleBookClick = useCallback(
    (bookName, testamentType) => {
      const bookKey = normalizeBookKey(bookName);
      const nextExpandedBookId = getExpandedBookId(testamentType, bookKey);

      prefetchWholeBookInBackground(testamentType, bookKey);

      setExpandedHomeBookId((previousValue) =>
        previousValue === nextExpandedBookId ? '' : nextExpandedBookId
      );
    },
    [getExpandedBookId]
  );

  const handleChapterClick = useCallback(
    async (testamentType, bookKey, chapterNumber, config) => {
      const nextOpeningChapterId = `${testamentType}:${bookKey}:${chapterNumber}`;

      if (openingHomeChapterId) return;

      setOpeningHomeChapterId(nextOpeningChapterId);

      const loadedVerses = await prefetchChapter(
        testamentType,
        bookKey,
        chapterNumber
      );

      setOpeningHomeChapterId('');

      if (loadedVerses && loadedVerses.length > 0) {
        navigate(getChapterPath(config, bookKey, chapterNumber), {
          state: {
            prefetchedVerses: loadedVerses,
            prefetchedBook: bookKey,
            prefetchedChapter: chapterNumber,
            prefetchedTestamentType: testamentType,
          },
        });
        return;
      }

      navigate(getChapterPath(config, bookKey, chapterNumber));
    },
    [getChapterPath, navigate, openingHomeChapterId, prefetchChapter]
  );

  const renderChaptersDropdown = useCallback(
    (bookName, testamentType, config) => {
      const bookKey = normalizeBookKey(bookName);
      const chapterCount = getBookChapterCount(testamentType, bookKey);

      if (!chapterCount) return null;

      const chapters = Array.from(
        { length: chapterCount },
        (_, index) => index + 1
      );

      const hasManyChapters = chapterCount > 100;

      return (
        <div
          className={`home-book-chapters-dropdown ${
            hasManyChapters ? 'home-book-chapters-dropdown-scroll' : ''
          }`}
        >
          {chapters.map((chapterNumber) => {
            const chapterOpeningId = `${testamentType}:${bookKey}:${chapterNumber}`;
            const isOpening = openingHomeChapterId === chapterOpeningId;

            return (
              <button
                key={`${bookKey}-${chapterNumber}`}
                type="button"
                className={`home-book-chapter-square ${
                  isOpening ? 'home-book-chapter-square-opening' : ''
                }`}
                onClick={() =>
                  handleChapterClick(
                    testamentType,
                    bookKey,
                    chapterNumber,
                    config
                  )
                }
                onMouseEnter={() =>
                  prefetchChapter(testamentType, bookKey, chapterNumber)
                }
                onFocus={() =>
                  prefetchChapter(testamentType, bookKey, chapterNumber)
                }
                onTouchStart={() =>
                  prefetchChapter(testamentType, bookKey, chapterNumber)
                }
                disabled={Boolean(openingHomeChapterId)}
              >
                {chapterNumber}
              </button>
            );
          })}
        </div>
      );
    },
    [handleChapterClick, openingHomeChapterId, prefetchChapter]
  );

  const renderBook = useCallback(
    (bookName, index, testamentType, config) => {
      const bookKey = normalizeBookKey(bookName);
      const currentExpandedBookId = getExpandedBookId(testamentType, bookKey);
      const isExpanded = expandedHomeBookId === currentExpandedBookId;

      return (
        <div
          key={bookName}
          className={`home-book-item ${
            isExpanded ? 'home-book-item-expanded' : ''
          }`}
        >
          <button
            type="button"
            className={`home-book-tile home-book-${testamentType}`}
            onClick={() => handleBookClick(bookName, testamentType)}
            onMouseEnter={() => handleBookPrefetch(bookName, testamentType)}
            onFocus={() => handleBookPrefetch(bookName, testamentType)}
            onTouchStart={() => handleBookPrefetch(bookName, testamentType)}
            aria-expanded={isExpanded}
          >
            <span className="home-book-index">{index + 1}</span>

            <span className="home-book-name">
              {getBookNameByLanguage(bookName, language)}
            </span>

            <span
              className={`home-book-dropdown-arrow ${
                isExpanded ? 'home-book-dropdown-arrow-open' : ''
              }`}
              aria-hidden="true"
            >
              <svg
                className="home-book-dropdown-arrow-icon"
                viewBox="0 0 24 24"
                focusable="false"
              >
                <path d="M7 10l5 5 5-5" />
              </svg>
            </span>
          </button>

          {isExpanded
            ? renderChaptersDropdown(bookName, testamentType, config)
            : null}
        </div>
      );
    },
    [
      expandedHomeBookId,
      getExpandedBookId,
      handleBookClick,
      handleBookPrefetch,
      language,
      renderChaptersDropdown,
    ]
  );

  const renderHomeSelect = ({
    id,
    value,
    options,
    disabled = false,
    onChange,
  }) => {
    const selectedOption =
      options.find((option) => option.value === value) || options[0];
    const isOpen = openSelectId === id;

    return (
      <div
        className={`home-custom-select ${
          disabled ? 'home-custom-select-disabled' : ''
        }`}
      >
        <button
          id={id}
          type="button"
          className="home-custom-select-button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() =>
            setOpenSelectId((previousValue) =>
              previousValue === id ? '' : id
            )
          }
        >
          <span className="home-custom-select-text">
            {selectedOption ? selectedOption.label : SELECT_TEXT}
          </span>

          <span className="home-custom-select-arrow" aria-hidden="true">
            <svg
              className="home-custom-select-arrow-icon"
              viewBox="0 0 24 24"
              focusable="false"
            >
              <path d="M7 10l5 5 5-5" />
            </svg>
          </span>
        </button>

        {isOpen && !disabled ? (
          <div
            className="home-custom-select-options"
            role="listbox"
            aria-labelledby={id}
          >
            {options.map((option) => (
              <button
                key={`${id}-${option.value}`}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={`home-custom-select-option ${
                  option.value === value ? 'active' : ''
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpenSelectId('');
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const handleLanguageToggle = (languageValue, setter) => {
    setter((previousValue) => {
      const isSelected = previousValue.includes(languageValue);

      if (isSelected && previousValue.length === 1) return previousValue;
      if (isSelected) {
        return previousValue.filter((item) => item !== languageValue);
      }

      return [...previousValue, languageValue];
    });
  };

  const renderLanguageSelection = (selectedValues, setter) => {
    const selectedSet = new Set(selectedValues);

    const orderedLanguageOptions = [
      ...selectedValues
        .map((selectedValue) =>
          LANGUAGE_OPTIONS.find((option) => option.value === selectedValue)
        )
        .filter(Boolean),
      ...LANGUAGE_OPTIONS.filter((option) => !selectedSet.has(option.value)),
    ];

    return (
      <div className="home-language-selection">
        {orderedLanguageOptions.map((option) => (
          <label key={option.value}>
            <input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={() => handleLanguageToggle(option.value, setter)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderQrCard = (type, title, IconComponent, imageSrc, appLink) => (
    <a
      href={appLink}
      target="_blank"
      rel="noopener noreferrer"
      className={`home-qr-card home-qr-card-${type}`}
      aria-label={`${title} - ${text.scanText}`}
    >
      <div className="home-qr-card-head">
        <span className="home-qr-card-icon">
          <IconComponent />
        </span>
        <h3>{title}</h3>
      </div>

      <div className="home-qr-image-box">
        <img src={imageSrc} alt={`${title} QR Code`} className="home-qr-image" />
      </div>

      <div className="home-qr-scan-label">
        <FaQrcode />
        <span>{text.scanText}</span>
      </div>
    </a>
  );

  const handleVerseTestamentChange = (value) => {
    setVerseForm((previousValue) => ({
      ...previousValue,
      testamentType: value,
      book: '',
      chapter: '',
    }));
  };

  const handleVerseBookChange = (value) => {
    setVerseForm((previousValue) => ({
      ...previousValue,
      book: value,
      chapter: '',
    }));

    if (value && verseForm.testamentType) {
      prefetchWholeBookInBackground(verseForm.testamentType, value);
    }
  };

  const handleWordTestamentChange = (value) => {
    setWordForm((previousValue) => ({
      ...previousValue,
      testamentType: value,
      book: ALL_VALUE,
      chapter: ALL_VALUE,
    }));

    setHomeKeyboardResetSignal((previousValue) => previousValue + 1);
  };

  const handleWordBookChange = (value) => {
    setWordForm((previousValue) => ({
      ...previousValue,
      book: value || ALL_VALUE,
      chapter: ALL_VALUE,
    }));

    if (value && value !== ALL_VALUE && wordForm.testamentType) {
      prefetchWholeBookInBackground(wordForm.testamentType, value);
    }
  };

  const handleHomeKeyboardToggle = () => {
    setShowHomeWordKeyboard((previousValue) => !previousValue);

    window.setTimeout(() => {
      if (wordInputRef.current) {
        try {
          wordInputRef.current.focus({ preventScroll: true });
        } catch (error) {
          wordInputRef.current.focus();
        }
      }
    }, 0);
  };

  const handleHomeKeyboardKeyPress = (key) => {
    setWordForm((previousValue) => {
      const nextWord = `${previousValue.word}${key}`;

      moveHomeWordCursorToEnd(nextWord);

      return {
        ...previousValue,
        word: nextWord,
      };
    });
  };

  const closeHomeKeyboard = () => {
    setShowHomeWordKeyboard(false);
  };

  const handleVerseReset = () => {
    setVerseForm({
      testamentType: '',
      book: '',
      chapter: '',
      verse: '',
    });
    setVerseLanguages(DEFAULT_SELECTED_LANGUAGES);
    setOpenSelectId('');
  };

  const handleWordReset = () => {
    setWordForm({
      testamentType: '',
      book: ALL_VALUE,
      chapter: ALL_VALUE,
      word: '',
    });
    setWordLanguages(DEFAULT_SELECTED_LANGUAGES);
    setShowHomeWordKeyboard(false);
    setHomeKeyboardResetSignal((previousValue) => previousValue + 1);
    setOpenSelectId('');
  };

  const handleVerseSubmit = (event) => {
    event.preventDefault();

    const params = new URLSearchParams();

    params.set('home', '1');
    params.set('testament', verseForm.testamentType || '');
    params.set('book', verseForm.book || '');
    params.set('chapter', verseForm.chapter || '');
    params.set('verse', verseForm.verse || '');
    params.set('languages', verseLanguages.join(','));

    navigate(`/bible-verse-find?${params.toString()}`, {
      state: {
        fromHome: true,
        source: 'home',
        type: 'verse',
        prefill: {
          testamentType: verseForm.testamentType,
          book: verseForm.book,
          chapter: verseForm.chapter,
          verse: verseForm.verse,
          selectedLanguages: verseLanguages,
        },
      },
    });
  };

  const handleWordSubmit = (event) => {
    event.preventDefault();

    const params = new URLSearchParams();

    params.set('home', '1');
    params.set('testament', wordForm.testamentType || '');
    params.set('book', wordForm.book || ALL_VALUE);
    params.set('chapter', wordForm.chapter || ALL_VALUE);
    params.set('word', wordForm.word || '');
    params.set('languages', wordLanguages.join(','));

    navigate(`/search-verse-by-word?${params.toString()}`, {
      state: {
        fromHome: true,
        source: 'home',
        type: 'word',
        prefill: {
          testamentType: wordForm.testamentType,
          book: wordForm.book,
          chapter: wordForm.chapter,
          word: wordForm.word,
          selectedLanguages: wordLanguages,
        },
      },
    });
  };

  return (
    <div className="home-container">
      <main className="home-main-section" ref={homeMainScrollRef}>
        <div className="home-verse-strip">
          <DailyVerse />
        </div>

        <div className="home-upper-grid">
          <section className="home-app-panel">
            <div className="home-app-body">
              <div className="home-qr-grid">
                {renderQrCard(
                  'ios',
                  text.iosTitle,
                  FaApple,
                  appStoreQr,
                  IOS_APP_LINK
                )}

                {renderQrCard(
                  'android',
                  text.androidTitle,
                  FaAndroid,
                  playStoreQr,
                  ANDROID_APP_LINK
                )}
              </div>
            </div>
          </section>

          <section className="home-bible-panel">
            <header className="home-panel-header home-bible-head">
              <div className="home-panel-title-row">
                <FaBook className="home-panel-header-icon" />
                <h2 className="home-panel-title">{text.title}</h2>
              </div>
            </header>

            <div className="home-testaments">
              <div className="home-testament-col home-testament-old-col">
                <div className="home-testament-col-head">
                  <span className="home-tcol-dot home-dot-old" />
                  <span className="home-tcol-label">{oldLabel}</span>
                  <span className="home-tcol-count">{oldBooks.length}</span>
                </div>

                <div className="home-testament-list">
                  {oldBooks.map((bookName, index) =>
                    renderBook(bookName, index, 'old', oldConfig)
                  )}
                </div>
              </div>

              <div className="home-testament-col home-testament-new-col">
                <div className="home-testament-col-head">
                  <span className="home-tcol-dot home-dot-new" />
                  <span className="home-tcol-label">{newLabel}</span>
                  <span className="home-tcol-count">{newBooks.length}</span>
                </div>

                <div className="home-testament-list">
                  {newBooks.map((bookName, index) =>
                    renderBook(bookName, index, 'new', newConfig)
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="home-search-row">
          <section className="home-feature-form-card home-feature-verse-form">
            <header className="home-panel-header home-feature-form-head">
              <div className="home-panel-title-row">
                <FaBookOpen className="home-panel-header-icon" />
                <h2 className="home-panel-title">{text.verseTitle}</h2>
              </div>
            </header>

            <form className="home-mini-form" onSubmit={handleVerseSubmit}>
              <div className="home-mini-form-row">
                <label htmlFor="home-verse-testament">{text.testament}</label>
                {renderHomeSelect({
                  id: 'home-verse-testament',
                  value: verseForm.testamentType,
                  options: verseTestamentOptions,
                  onChange: handleVerseTestamentChange,
                })}
              </div>

              <div className="home-mini-form-row">
                <label htmlFor="home-verse-book">{text.book}</label>
                {renderHomeSelect({
                  id: 'home-verse-book',
                  value: verseForm.book,
                  options: verseBookOptions,
                  disabled: !verseForm.testamentType,
                  onChange: handleVerseBookChange,
                })}
              </div>

              <div className="home-mini-form-row">
                <label htmlFor="home-verse-chapter">{text.chapter}</label>
                {renderHomeSelect({
                  id: 'home-verse-chapter',
                  value: verseForm.chapter,
                  options: verseChapterOptions,
                  disabled: !verseForm.book,
                  onChange: (value) =>
                    setVerseForm((previousValue) => ({
                      ...previousValue,
                      chapter: value,
                    })),
                })}
              </div>

              <div className="home-mini-form-row">
                <label htmlFor="home-verse-number">{text.verse}</label>
                <input
                  id="home-verse-number"
                  type="text"
                  dir="ltr"
                  value={verseForm.verse}
                  onChange={(event) =>
                    setVerseForm((previousValue) => ({
                      ...previousValue,
                      verse: event.target.value,
                    }))
                  }
                  placeholder={text.versePlaceholder}
                />
              </div>

              <div className="home-mini-form-row home-language-row">
                <label>{text.language}</label>
                {renderLanguageSelection(verseLanguages, setVerseLanguages)}
              </div>

              <div className="home-submit-reset-buttons-column">
                <button
                  type="submit"
                  className="home-mini-submit-button home-mini-submit-verse"
                >
                  {text.submit}
                </button>

                <button
                  type="button"
                  className="home-mini-reset-button"
                  onClick={handleVerseReset}
                >
                  {text.reset}
                </button>
              </div>
            </form>
          </section>

          <section
            className="home-feature-form-card home-feature-word-form"
            ref={homeWordCardRef}
          >
            <header className="home-panel-header home-feature-form-head">
              <div className="home-panel-title-row">
                <FaSearch className="home-panel-header-icon" />
                <h2 className="home-panel-title">{text.wordTitle}</h2>
              </div>
            </header>

            <form
              className="home-mini-form"
              onSubmit={handleWordSubmit}
              autoComplete="off"
            >
              <div className="home-mini-form-row">
                <label htmlFor="home-word-testament">{text.testament}</label>
                {renderHomeSelect({
                  id: 'home-word-testament',
                  value: wordForm.testamentType,
                  options: wordTestamentOptions,
                  onChange: handleWordTestamentChange,
                })}
              </div>

              <div className="home-mini-form-row">
                <label htmlFor="home-word-book">{text.book}</label>
                {renderHomeSelect({
                  id: 'home-word-book',
                  value: wordForm.book,
                  options: wordBookOptions,
                  disabled: !wordForm.testamentType,
                  onChange: handleWordBookChange,
                })}
              </div>

              <div className="home-mini-form-row">
                <label htmlFor="home-word-chapter">{text.chapter}</label>
                {renderHomeSelect({
                  id: 'home-word-chapter',
                  value: wordForm.chapter,
                  options: wordChapterOptions,
                  disabled:
                    !wordForm.testamentType || wordForm.book === ALL_VALUE,
                  onChange: (value) =>
                    setWordForm((previousValue) => ({
                      ...previousValue,
                      chapter: value || ALL_VALUE,
                    })),
                })}
              </div>

              <div
                className="home-mini-form-row home-word-input-row"
                ref={wordInputRowRef}
              >
                <label htmlFor="home-word-input">{text.word}</label>

                <div className="home-word-input-wrap">
                  <input
                    ref={wordInputRef}
                    id="home-word-input"
                    name="home-tamil-word-search-no-autocomplete"
                    type="text"
                    dir="ltr"
                    value={wordForm.word}
                    onChange={(event) =>
                      setWordForm((previousValue) => ({
                        ...previousValue,
                        word: event.target.value,
                      }))
                    }
                    placeholder={text.wordPlaceholder}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />

                  <button
                    ref={homeWordKeyboardToggleRef}
                    type="button"
                    className={`home-word-keyboard-toggle ${
                      showHomeWordKeyboard
                        ? 'home-word-keyboard-toggle-active'
                        : ''
                    }`}
                    onClick={handleHomeKeyboardToggle}
                    aria-label={text.keyboard}
                    title={text.keyboard}
                  >
                    <FaKeyboard />
                  </button>
                </div>
              </div>

              <div className="home-mini-form-row home-language-row">
                <label>{text.language}</label>
                {renderLanguageSelection(wordLanguages, setWordLanguages)}
              </div>

              <div className="home-submit-reset-buttons-column">
                <button
                  type="submit"
                  className="home-mini-submit-button home-mini-submit-word"
                >
                  {text.submit}
                </button>

                <button
                  type="button"
                  className="home-mini-reset-button"
                  onClick={handleWordReset}
                >
                  {text.reset}
                </button>
              </div>
            </form>

            {showHomeWordKeyboard ? (
              <div
                className="home-word-keyboard-panel"
                ref={homeWordKeyboardRef}
                style={{
                  top: `${homeKeyboardPosition.top}px`,
                  maxHeight: homeKeyboardPosition.ready
                    ? `${homeKeyboardPosition.maxHeight}px`
                    : 'none',
                  opacity: homeKeyboardPosition.ready ? 1 : 0,
                  pointerEvents: homeKeyboardPosition.ready ? 'auto' : 'none',
                }}
              >
                <button
                  type="button"
                  className="home-word-keyboard-close"
                  onClick={closeHomeKeyboard}
                  aria-label={text.closeKeyboard}
                  title={text.closeKeyboard}
                >
                  <FaTimes />
                </button>

                <TamilKeyboard
                  onKeyPress={handleHomeKeyboardKeyPress}
                  resetSignal={homeKeyboardResetSignal}
                />
              </div>
            ) : null}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Home;